import _ from 'underscore';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { TilesGridStateless } from './tiles-grid';
import { MultiSelectionGrid } from './multi-selection-grid';
import { getSectionTilesGridStyle } from './styles/section-tiles-grid-style';
import { DEFAULT_CONFIG } from './default-grid-config';

const SectionTilesGridInternal = ({
  sectionData, onRenderCell, onRenderLoadingCell, loadNextPage, multiSelectionEnabled, onSelectionChange, classes, ...otherProps
}) => {
  const [dimensions, setDimensions] = useState({
    colCount: 1,
    colWidth: DEFAULT_CONFIG.maxColumnWidth,
    surfaceWidth: 0,
  });

  const getGridSize = useCallback((section) => {
    const config = section.config || DEFAULT_CONFIG;
    let finalColWidth;
    let rowHeight;
    const { items } = _.max(sectionData, sec => sec.items && sec.items.length) || {};
    if (!_.isEmpty(items) && items.length < dimensions.colCount) {
      // use max width if items are not enough to fill a row
      finalColWidth = config.maxColumnWidth;
      rowHeight = config.aspectRatio ? config.maxColumnWidth * config.aspectRatio : config.rowHeight;
    } else {
      finalColWidth = dimensions.surfaceWidth > 0 ? `${100 * (dimensions.colWidth / dimensions.surfaceWidth)}%` : dimensions.colWidth;
      rowHeight = config.aspectRatio ? dimensions.colWidth * config.aspectRatio : config.rowHeight;
    }
    return { colWidth: finalColWidth, rowHeight };
  }, [dimensions, sectionData]);

  //
  useEffect(() => {
    if (!sectionData || sectionData.length <= 0) {
      return;
    }
    // if any section has 0 items or has fully loaded, load next section
    let sectionIndexToLoad = -1;
    for (let idx = 0; idx < sectionData.length; idx += 1) {
      const { items, totalItemCount } = sectionData[idx];
      if (items === null) {
        // not loaded yet, load this section
        sectionIndexToLoad = idx;
        break;
      } else if (items.length >= totalItemCount) {
        // this section is fully loaded, may need to load next section
        // need to further check next sections
        sectionIndexToLoad = idx + 1;
      } else if (items.length < totalItemCount) {
        if (items.length === 0) {
          // load this section
          sectionIndexToLoad = idx;
        } else if (items.length > 0) {
          // this section is partially loaded, loading cell will load following pages
          sectionIndexToLoad = -1;
        }
        break;
      }
    }

    // suppose initial loading of section 0 is triggered outside
    if (sectionIndexToLoad > 0) {
      const { sectionName } = sectionData[sectionIndexToLoad] || {};
      if (sectionName) {
        loadNextPage({ sectionName, startLoadingNextSection: true });
      }
    }
  }, [loadNextPage, sectionData]);

  const wrappedSectionData = useMemo(() => sectionData.map(section => ({
    loadNextPage: () => {
      const { sectionName } = section || {};
      // invoke callback prop with sectionName parameter
      loadNextPage({ sectionName, startLoadingNextSection: false });
    },
    onRenderCell: (params) => {
      const { sectionName } = section || {};
      return onRenderCell({ ...params, sectionName });
    },
    onRenderLoadingCell: ({ item, index, onLoading }) => {
      const { sectionName } = section || {};
      if (onRenderLoadingCell) {
        return onRenderLoadingCell({
          item, index, onLoading, sectionName,
        });
      }
      return null;
    },
    // to support selectoin on grid
    onSelectionChange: onSelectionChange ?
      (params) => {
        const { sectionName } = section || {};
        onSelectionChange({ ...params, sectionName });
      } : undefined,
    gridSize: getGridSize(section),
    ...section,
  })), [
    getGridSize,
    loadNextPage,
    onRenderCell,
    onRenderLoadingCell,
    onSelectionChange,
    sectionData,
  ]);

  // don't show section title if only one section has data
  const countOfSectionsWithData =
    _.filter(sectionData, sec => sec.totalItemCount > 0).length;

  const GridComponent = multiSelectionEnabled ? MultiSelectionGrid : TilesGridStateless;

  return (
    <div className={classnames(classes.sectionTilesGridContainer, classes.gridConainerClass)}>
      {wrappedSectionData.map(({
        sectionName,
        sectionTitle,
        sectionClassName,
        items,
        gridSize,
        ...rest
      }) => (
        (items && items.length > 0) ?
          <div
            key={sectionName}
            className={classnames(sectionClassName, 'tiles-grid-section')}
          >
            {countOfSectionsWithData > 1 ? <div className="section-title">{sectionTitle}</div> : null}
            <GridComponent
              items={items}
              {...rest}
              {...otherProps}
              ariaLabel={countOfSectionsWithData > 1 ? sectionTitle : ''}
              dimensions={dimensions}
              setDimensions={setDimensions}
              gridSize={gridSize}
              version={gridSize}
            />
          </div>
       : null
      ))}
    </div>
  );
};

SectionTilesGridInternal.propTypes = {
  sectionData: PropTypes.arrayOf(PropTypes.shape({
    sectionName: PropTypes.string,
    sectionTitle: PropTypes.string,
    sectionClassName: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({})),
    selectedItems: PropTypes.arrayOf(PropTypes.shape({})), // to support multiple selection
    totalItemCount: PropTypes.number, // total item count, including not loaded items
    config: PropTypes.shape({
      aspectRatio: PropTypes.number,
      maxColumnWidth: PropTypes.number,
      minColumnWidth: PropTypes.number,
      rowHeight: PropTypes.number,
      preferredCellsPerPage: PropTypes.number,
    }),
  })).isRequired,
  onRenderCell: PropTypes.func.isRequired,
  onRenderLoadingCell: PropTypes.func,
  loadNextPage: PropTypes.func.isRequired,

  multiSelectionEnabled: PropTypes.bool,
  onSelectionChange: PropTypes.func,

  classes: PropTypes.shape({
    sectionTilesGridContainer: PropTypes.string,
    gridConainerClass: PropTypes.string,
  }).isRequired,
};

SectionTilesGridInternal.defaultProps = {
  onRenderLoadingCell: undefined,

  multiSelectionEnabled: false,
  onSelectionChange: undefined,
};

export const SectionTilesGrid = withDefaultStyles(SectionTilesGridInternal, getSectionTilesGridStyle);
